import { getSupabaseClient } from '@/lib/supabase/client.ts'
import type { TenantRow, TenantWorkingHoursRow } from '@/lib/supabase/database.types.ts'

import { businessSchema, type Business, type WorkingDay } from '../model/business.ts'

/** Real Supabase read for a studio's public profile. No fallback here. */
export async function fetchBusinessProfile(tenantSlug: string): Promise<Business | null> {
  const client = getSupabaseClient()

  const { data: tenant, error: tenantError } = await client
    .from('tenants')
    .select('*')
    .eq('slug', tenantSlug)
    .maybeSingle()

  if (tenantError !== null) throw new Error(tenantError.message)
  if (tenant === null) return null

  const row = tenant as unknown as TenantRow

  const { data: hours, error: hoursError } = await client
    .from('tenant_working_hours')
    .select('*')
    .eq('tenant_id', row.id)

  if (hoursError !== null) throw new Error(hoursError.message)

  const byWeekday = new Map<number, TenantWorkingHoursRow>(
    (hours ?? []).map((item) => {
      const hour = item as unknown as TenantWorkingHoursRow
      return [hour.weekday, hour]
    }),
  )

  // A missing row means closed. The schema wants all seven days present, so
  // the gaps are filled here rather than leaving the UI to guess.
  const workingDays: WorkingDay[] = [0, 1, 2, 3, 4, 5, 6].map((weekday) => {
    const hour = byWeekday.get(weekday)
    if (hour === undefined) {
      return { weekday } as WorkingDay
    }
    return {
      weekday,
      opensAt: hour.opens_at.slice(0, 5),
      closesAt: hour.closes_at.slice(0, 5),
    } as WorkingDay
  })

  return businessSchema.parse({
    name: row.name,
    heroUrl: row.hero_path ? client.storage.from('work-photos').getPublicUrl(row.hero_path).data.publicUrl : '',
    logoUrl: row.logo_path ? client.storage.from('work-photos').getPublicUrl(row.logo_path).data.publicUrl : '',
    accent: [row.accent_light, row.accent_dark],
    tagline: row.tagline,
    about: row.about,
    address: row.address,
    addressNote: row.address_note,
    phone: row.phone,
    phoneDisplay: row.phone_display,
    workingDays,
    timezone: row.timezone,
    socials: { max: row.max_url, telegram: row.telegram_url, whatsapp: row.whatsapp_url },
  })
}
